import type { FulfillmentLocationQty } from '@/infrastructure/FulfillmentAdminClient';

/**
 * Where one printed row's units sit. A line's stock may come from more than one shelf (SSD-7236); the server
 * sends that list as `locations`, and the printed sheets give a split line one row per shelf.
 */

/** The retired V1 mirror's reserved counter: a number, never a shelf anything can be booked into. */
export const V1_RESERVED_LOCATION = 'V1#RESERVED';
/** V1's one-bucket warehouse: real stock, but no shelf anyone can walk to, so a real shelf outranks it. */
export const V1_WAREHOUSE_LOCATION = 'WH#00';

const isVirtual = (locationCode: string | null): boolean =>
  locationCode === V1_RESERVED_LOCATION || locationCode === V1_WAREHOUSE_LOCATION;

/**
 * The server's ranking, so the first entry is the line's primary location: the largest share first, a
 * real shelf before a virtual one on a tie, then by code; a slice pinned to no shelf goes last.
 */
export const rankLocations = (locations: FulfillmentLocationQty[]): FulfillmentLocationQty[] =>
  [...locations].sort(
    (a, b) =>
      b.qty - a.qty ||
      Number(isVirtual(a.locationCode)) - Number(isVirtual(b.locationCode)) ||
      Number(a.locationCode === null) - Number(b.locationCode === null) ||
      (a.locationCode ?? '').localeCompare(b.locationCode ?? ''),
  );

/**
 * Whether the line's stock comes from more than one place, a share on no shelf counting as a place of its
 * own: that is when the sheets cut the row.
 */
export const isSplitAcrossLocations = (locations: FulfillmentLocationQty[] | undefined): boolean =>
  (locations ?? []).length > 1;

/**
 * A printed row per shelf. A row that still carries the whole line split across several shelves (its qty
 * is its locations' total) becomes one row per shelf at that shelf's share, so a picker or packer walks to
 * each; a row the server already cut per shelf, or one from a single shelf, stays as it is.
 */
export const splitAcrossLocations = <R extends { qty: number; locations?: FulfillmentLocationQty[] }>(
  row: R,
  placed: (row: R, location: FulfillmentLocationQty) => R,
): R[] => {
  const locations = rankLocations(row.locations ?? []);
  const whole = locations.reduce((sum, location) => sum + location.qty, 0) === row.qty;
  return isSplitAcrossLocations(locations) && whole ? locations.map((location) => placed(row, location)) : [row];
};
